import { z } from "zod";
import { NativeCalendarDateSchema, NativeCalendarPhraseSchema, isNativeCalendarFollowUpEvent } from "./native-calendar.js";
/**
 * The one place that knows how the calendar marks a series: `series` of a calendar event
 * (native_calendar_events.series_key, migration 303). An event without one is a series of its own.
 */
export function seriesOf(event) {
    return event.series ? { key: event.series.key, title: event.series.title } : { key: event.id, title: event.title };
}
/** The part of the series an event's people count in: its own day, or the дожим for a follow-up event. */
export function webinarPartOf(event) {
    return isNativeCalendarFollowUpEvent(event) ? "follow_up" : webinarDayPart(event.id);
}
/**
 * Whether a webinar has started: its date and time in Moscow (UTC+3 all year) are not after `now`; a day without a time
 * starts at its midnight. A started day shows its numbers while it is still on air (Ден 20.10.2026, 21:36, the first day of a
 * series: «и где аналитика?» — the list waited for the next day); one not started yet shows its readiness.
 */
export function webinarStarted(event, now) {
    return webinarStartsAt(event) <= +now;
}
/** The instant (ms) a webinar starts, as `webinarStarted` reads it. */
export function webinarStartsAt(event) {
    return Date.parse(`${event.date}T${event.time ?? "00:00"}:00+03:00`);
}
/** Events grouped by `seriesOf`, series in the order of their first day, days in date order. */
export function groupWebinarSeries(events) {
    const groups = new Map();
    for (const event of [...events].sort((left, right) => left.date.localeCompare(right.date) || left.title.localeCompare(right.title))) {
        const ref = seriesOf(event);
        const group = groups.get(ref.key) ?? { key: ref.key, title: ref.title, events: [] };
        group.events.push(event);
        groups.set(ref.key, group);
    }
    return [...groups.values()];
}
export const WebinarSeriesKeySchema = z.string().min(1).max(200);
// ————— Numbers —————
const Count = z.number().int().nonnegative();
const Money = z.number().nonnegative();
const OutcomeFields = { people: Count, booked: Count, held: Count, bought: Count, cash: Money };
/**
 * The deals these people bought by (Паша 01.10.2026: «общая сумма сделок — сколько с них в итоге ждём»), as the
 * analytics counts them (webinar-page.series.v4): the deals of their new-sale payments after the first code word, each
 * once. `sum` — Σ the deal's full sum (contract), `outstanding` — «ещё ждём», Σ the debt of the open deals (both from
 * the core's deal finance, Паша 26.09.2026); `withoutSum` — deals with no sum at all (no payments-table row, no amo
 * budget) or purchases without a deal.
 */
export const WebinarDealsSchema = z.object({
    count: Count, sum: Money, outstanding: Money, withoutSum: Count,
}).strict();
/**
 * Segments (Ден 21.10.2026, core webinar-page.series.v8): who the person was before the series, by their own facts up to the
 * day of the first code word: a payment → client; a held consultation (Vekha's calendar from 17.09, amo before) → warm; a
 * request or a booking, no consultation → back; nothing → new. No CRM stage or pipeline in the rule. `fromLost` — those of
 * the segment «из отказа»: before that day a deal of theirs was closed without a purchase (only warm and back have any).
 */
export const WEBINAR_SEGMENTS = ["new", "warm", "back", "client"];
export const WebinarSegmentSchema = z.enum(WEBINAR_SEGMENTS);
const SegmentOutcomesSchema = z.object({ ...OutcomeFields, deals: WebinarDealsSchema.nullable() }).strict();
export const WebinarSegmentCountsSchema = SegmentOutcomesSchema.extend({ fromLost: SegmentOutcomesSchema }).strict();
const SegmentsSchema = z.object({ new: WebinarSegmentCountsSchema, warm: WebinarSegmentCountsSchema, back: WebinarSegmentCountsSchema,
    client: WebinarSegmentCountsSchema }).strict();
/** Outcomes of some people and how many of them are new deals, repeat requests and outside ОП. */
export const WebinarOutcomeCountsSchema = z.object({
    ...OutcomeFields, new: Count, repeat: Count, outside: Count,
    /** Null — the analytics release does not count deal sums yet. */
    deals: WebinarDealsSchema.nullable(),
    /** Installments these people paid for purchases made before the series (Ден 27.10.2026): apart from `cash`, which is
     * only what they bought from it. Absent — an analytics release before webinar-page.series.v9 (`cash` holds both). */
    cashEarlier: Money.optional(),
    /** Ден 29.10.2026: people whose deal had only a deposit before the series and was paid on after it — «доплатили по
     * прежней предоплате», apart from «купили» and `cash`. Absent — an analytics release before webinar-page.series.v11. */
    toppedUp: Count.optional(), cashToppedUp: Money.optional(),
    /** The same people by segment; each person in one, so the four add up to the counts above. Absent — an analytics
     * release before segments: the page shows the groups. */
    segments: SegmentsSchema.optional(),
}).strict();
export const WEBINAR_GROUPS = ["new_deal", "repeat_request", "outside_sales"];
export const WebinarGroupSchema = z.enum(WEBINAR_GROUPS);
export const WebinarCodeWordPurposeSchema = z.enum(["application", "bonus", "follow_up", "offer"]);
export const WebinarCodeWordSummarySchema = WebinarOutcomeCountsSchema.extend({
    purpose: WebinarCodeWordPurposeSchema,
    /** The phrases of this purpose, as written in the calendar. */
    phrases: z.array(z.string()),
}).strict();
export const WebinarSummarySchema = WebinarOutcomeCountsSchema.extend({
    groups: z.object({ new_deal: WebinarOutcomeCountsSchema, repeat_request: WebinarOutcomeCountsSchema,
        outside_sales: WebinarOutcomeCountsSchema }).strict(),
    codeWords: z.array(WebinarCodeWordSummarySchema),
}).strict();
/** `event:<id>` — a day of the series; `follow_up` — the дожим. */
export const WebinarPartKeySchema = z.string().regex(/^(?:event:[0-9a-f-]{36}|follow_up)$/u);
export function webinarDayPart(eventId) { return `event:${eventId}`; }
export const WebinarPartSchema = z.object({
    key: WebinarPartKeySchema, eventId: z.string().uuid().nullable(), title: z.string(),
    date: NativeCalendarDateSchema.nullable(), summary: WebinarSummarySchema,
    /** «Откликнулись повторно»: came to the series earlier and wrote this part's words later — not in the sum. */
    returned: WebinarOutcomeCountsSchema,
}).strict();
/** ready — the analytics has it; upcoming — not happened yet; pending — not in the analytics data yet. */
export const WebinarAnalyticsStateSchema = z.enum(["ready", "upcoming", "pending"]);
const Freshness = {
    /** When the analytics last checked its sources (people.timeline), ISO. */
    dataAsOf: z.string().datetime({ offset: true }).nullable(),
    /** When Vekha read it (cached for five minutes), ISO. */
    fetchedAt: z.string().datetime({ offset: true }),
};
// ————— List: «Календарь → Эфиры» —————
export const WebinarAnalyticsListQuerySchema = z.object({
    from: NativeCalendarDateSchema, to: NativeCalendarDateSchema,
}).strict().refine(({ from, to }) => from <= to && Date.parse(to) - Date.parse(from) <= 62 * 86_400_000, { message: "Период — не больше двух месяцев." });
export const WebinarAnalyticsListSeriesSchema = z.object({
    key: WebinarSeriesKeySchema, state: WebinarAnalyticsStateSchema, summary: WebinarOutcomeCountsSchema.nullable(),
    /** Days of the series (and the дожим): each day's part of the series numbers. */
    parts: z.array(z.object({ key: WebinarPartKeySchema, eventId: z.string().uuid().nullable(),
        state: WebinarAnalyticsStateSchema, summary: WebinarOutcomeCountsSchema.nullable() }).strict()),
}).strict();
export const WebinarAnalyticsListSchema = z.object({
    ...Freshness, series: z.array(WebinarAnalyticsListSeriesSchema),
}).strict();
// ————— A series or one of its days —————
export const WebinarDaySchema = z.object({ ...OutcomeFields, date: NativeCalendarDateSchema }).strict();
/** How «old» a repeat request is, from facts before the series (the analytics' groups, strongest first). */
export const WEBINAR_HISTORY = ["bought", "consulted", "returns", "first_return"];
export const WebinarHistorySchema = z.enum(WEBINAR_HISTORY);
/** Where a person came from at the day of their first code word or request (core person_source, the rule of «Реклама»):
 * an ad campaign (`ad: true`) or why not from ads, with the marks of their last registration — source, medium, campaign. */
export const MarketingPersonSourceSchema = z.object({
    ad: z.boolean(), group: z.string().max(200), label: z.string().max(300), marks: z.array(z.string().max(300)).max(3).optional(),
}).strict();
export const WebinarPersonSchema = z.object({
    personId: z.string().min(1).max(40),
    name: z.string().nullable(),
    /** The first code word of the series, ISO. */
    firstAt: z.string().datetime({ offset: true }),
    /** The day (or the дожим) the person counts in — of their first code word in the series — and its word. */
    part: WebinarPartKeySchema,
    /** Other parts they wrote words of later («писал ещё»), in order. */
    laterParts: z.array(WebinarPartKeySchema),
    entryEventId: z.string().uuid().nullable(),
    entryPurpose: WebinarCodeWordPurposeSchema,
    codeWords: z.array(z.object({ eventId: z.string().uuid().nullable(), purpose: WebinarCodeWordPurposeSchema }).strict()),
    group: WebinarGroupSchema,
    /** The segment (WEBINAR_SEGMENTS) and «из отказа»; absent — an analytics release before segments. */
    segment: WebinarSegmentSchema.optional(), fromLost: z.boolean().optional(),
    /** What the person had before: новый, был в базе, оставлял заявку, записывался, был на консультации, покупал. */
    before: z.string(),
    booked: z.boolean(), bookedOn: NativeCalendarDateSchema.nullable(),
    held: z.boolean(), heldOn: NativeCalendarDateSchema.nullable(),
    bought: z.boolean(), boughtOn: NativeCalendarDateSchema.nullable(),
    cash: Money,
    /** Paid on after the series a deal that had only a deposit before it (Ден 29.10.2026); absent before series.v11. */
    toppedUp: z.boolean().optional(), cashToppedUp: Money.optional(),
    /** The deals the person bought by, with their full sum (null — no sum) and what is still awaited. */
    purchases: z.array(z.object({
        dealId: z.string().regex(/^\d{1,19}$/u), dealUrl: z.string().url(), contract: Money.nullable(), outstanding: Money,
    }).strict()),
    /** A purchase whose payment names no deal: counted in «без суммы». */
    purchaseWithoutDeal: z.boolean(),
    /** A repeat request's group; null for a new deal or outside ОП. */
    history: WebinarHistorySchema.nullable(),
    /** First entry into ОП before the series, months in ОП by then, entries into ОП and those from webinars. */
    salesSince: NativeCalendarDateSchema.nullable(), salesMonths: Count.nullable(),
    salesEntries: Count, webinarEntries: Count,
    /** «ОП · Выбор времени» — pipeline and stage of the person's deal now; empty without a deal. */
    nowStage: z.string(),
    dealId: z.string().regex(/^\d{1,19}$/u).nullable(),
    dealUrl: z.string().url().nullable(),
    /** Where they came from (17.10); absent from an older analytics, null when the core does not know. */
    source: MarketingPersonSourceSchema.nullable().optional(),
}).strict();
export const WebinarEventInfoSchema = z.object({
    id: z.string().uuid(), title: z.string(), date: NativeCalendarDateSchema, time: z.string().nullable(),
    campaignKey: z.string(), phrases: z.array(NativeCalendarPhraseSchema),
}).strict();
/**
 * «Регистрации» (Ден 20.10.2026: «то, что мы сами регистрируем на эфир через нашу систему, должно отображаться в формах и
 * в эфирах»; GetCourse и /rt — вне аналитики): people registered for the series by the Mini App's screen «Регистрация на
 * эфир» or a site form «Эфир», one each, by their first registration — counted by the analytics (webinar.page
 * `registered`), Vekha only shows. A registration is the whole series' (its first day owns it).
 */
export const WebinarRegistrationSourceSchema = z.enum(["form", "link"]);
export const WebinarRegistrantSchema = z.object({
    personId: z.string().min(1).max(40), name: z.string().nullable(), at: z.string().datetime({ offset: true }),
    by: WebinarRegistrationSourceSchema,
    /** The site form of the first registration; null — the Mini App screen. */
    formId: z.string().max(60).nullable(),
    /** Also came to the series: wrote its code word or left a request by its link. */
    came: z.boolean(),
    dealId: z.string().regex(/^\d{1,19}$/u).nullable(), dealUrl: z.string().url().nullable(), nowStage: z.string(),
}).strict();
export const WebinarRegistrationsSchema = z.object({
    people: Count, form: Count, link: Count,
    /** By site form, the biggest first; `name` — the form's name in «Сайты → Формы», null when it is not there. */
    forms: z.array(z.object({ id: z.string().max(60), name: z.string().nullable(), people: Count }).strict()),
    list: z.array(WebinarRegistrantSchema),
}).strict();
/**
 * «Зрителей в пике» (Ден 20.10.2026): how many people watched a webinar day at its peak, entered by hand by whoever edits
 * webinars until the broadcast platform tells it by itself. Kept in Vekha per day (vekha.webinar_peak_viewers, every save in
 * its history with who and when). Empty is «не внесено», never 0: a 0 is someone's number.
 */
export const WEBINAR_PEAK_VIEWERS_MAX = 100_000;
export const WebinarPeakViewersValueSchema = z.number().int().min(0).max(WEBINAR_PEAK_VIEWERS_MAX).nullable();
export const WebinarPeakViewersEntrySchema = z.object({
    viewers: WebinarPeakViewersValueSchema, at: z.string().datetime({ offset: true }), by: z.string().max(200).nullable(),
}).strict();
export const WebinarPeakViewersSchema = z.object({
    eventId: z.string().uuid(), viewers: WebinarPeakViewersValueSchema,
    /** 0 — never entered. */
    version: Count,
    /** The last save, the newest first; empty — never entered. */
    history: z.array(WebinarPeakViewersEntrySchema).max(50),
}).strict();
export const WebinarPeakViewersMutationSchema = z.object({
    idempotencyKey: z.string().min(16).max(160), expectedVersion: Count, viewers: WebinarPeakViewersValueSchema,
}).strict();
/** A day's peak on the analytics page: null — «не внесено». */
export const WebinarPeakSchema = z.object({ eventId: z.string().uuid(), viewers: WebinarPeakViewersValueSchema }).strict();
export const WebinarAnalyticsDetailSchema = z.object({
    ...Freshness,
    series: z.object({ key: WebinarSeriesKeySchema, title: z.string(), events: z.array(WebinarEventInfoSchema).min(1) }).strict(),
    /** The day the page is about; null — the whole series. */
    focusEventId: z.string().uuid().nullable(),
    /** The part that day counts: `event:<id>`, or `follow_up` for the series follow-up event. */
    focusPart: WebinarPartKeySchema.nullable(),
    state: WebinarAnalyticsStateSchema,
    /** The whole series and its parts (days, then the дожим). */
    summary: WebinarSummarySchema.nullable(),
    parts: z.array(WebinarPartSchema),
    /** Of the focus day when there is one, else of the series. */
    days: z.array(WebinarDaySchema),
    /** People whose outcome the rows could not date (normally zero). */
    undated: z.object({ booked: Count, held: Count, bought: Count }).strict(),
    stages: z.array(z.object({ pipeline: z.string(), stage: z.string(), people: Count }).strict()),
    before: z.array(z.object({ label: z.string(), people: Count }).strict()),
    /** Repeat requests by how «old» they are, with the median months in ОП. */
    history: z.array(z.object({ key: WebinarHistorySchema, label: z.string(), people: Count,
        medianMonths: z.number().nonnegative().nullable() }).strict()),
    /** Everyone of the focus day when there is one, else of the series; each with the part they count in. */
    people: z.array(WebinarPersonSchema),
    /** How the analytics counts these numbers, for the page footnote. */
    definition: z.string().nullable(),
    /** «Регистрации» of the series; null — an analytics release before them, or the page is not about the series or its first day. */
    registrations: WebinarRegistrationsSchema.nullable().default(null),
    /** «Зрителей в пике» of every event of the series, in its order (`series.events`); entered in Vekha by hand. */
    peaks: z.array(WebinarPeakSchema).default([]),
}).strict();
//# sourceMappingURL=webinar-analytics.js.map